/**
 * THE RIGHT RAIL (P39, Roman's decision D6) — reference §6: *"Two columns with
 * a 392 px right rail. Left: status and actions, the detected facts, the draft,
 * the case file. Right: Details and History."*
 *
 * =========================================================================
 * WHAT IS A DETAIL AND WHAT IS A FACT
 * =========================================================================
 * The split is not "important / unimportant", which would be a taste. It is
 * **is there a control for it on this screen**:
 *
 * - The left column holds the four facts a person can CHANGE — customer,
 *   article, document type and language, priority and due — because each has a
 *   *Change* link whose editor opens directly beneath it. Roman, 2026-09-03:
 *   *"die änderungsmöglichkeiten sind viel zu weit unten und weg von dem ort von
 *   dem man geklickt hat."* A link in the rail that opened a panel in the other
 *   column would be that complaint again, one column over.
 * - This card holds what the ticket merely REPORTS: which company it belongs to,
 *   where its authorization stands, how the agent read the mail, and when
 *   anything last happened. Nothing here is editable, and nothing here has a
 *   control anywhere else on the screen either.
 *
 * The authorization is the one that looks editable and is not: a ticket
 * `AwaitingSalesApproval` carries a LINK to `/authorizations`, because the
 * decision is a Sales Manager's and is made there. That link was on the ticket
 * before this phase (P11) and moves unchanged.
 *
 * =========================================================================
 * HISTORY IS THE LEDGER, NOT A SUMMARY OF IT
 * =========================================================================
 * The audit list moves here verbatim from the second of the two tabs the screen
 * used to carry. Two tabs for "what a person wrote" and "what the system
 * recorded" made the reader choose between them, and the ledger is the thing
 * you read WHILE looking at something else — which is what a rail is for. The
 * case file stays in the work column, because adding to it is an action.
 */
import { Link } from 'react-router-dom';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Confidence } from './ui/Confidence';
import { AUTHORIZATION_STATUS_LABELS, companyLabel, companyTooltip, labelFor, tooltipFor } from '../lib/labels';
import { isoDate, isoDateTime } from '../lib/dates';
import type { AuditEntry, Ticket } from '../types/domain';

/** One row of the rail: a label, and the value under it. */
function Detail({ label, title, children }: { label: string; title?: string; children: React.ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className="text-xs text-muted">{label}</dt>
            <dd className="mt-0.5 min-w-0 text-sm text-body" title={title}>{children}</dd>
        </div>
    );
}

export function TicketDetailsCard({ ticket }: { ticket: Ticket }) {
    return (
        <Card title="Details">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Detail label="Company" title={companyTooltip(ticket.companyCode)}>
                    {ticket.companyCode ? companyLabel(ticket.companyCode) : '—'}
                </Detail>

                <Detail
                    label="Authorization"
                    title={ticket.status === 'AwaitingSalesApproval'
                        ? undefined
                        : `${tooltipFor(AUTHORIZATION_STATUS_LABELS, ticket.authorizationStatus)}\n\nCode: ${ticket.authorizationStatus}`}
                >
                    {ticket.status === 'AwaitingSalesApproval' ? (
                        <Link
                            to="/authorizations"
                            className="text-info hover:underline"
                            title={tooltipFor(AUTHORIZATION_STATUS_LABELS, ticket.authorizationStatus)}
                        >
                            {labelFor(AUTHORIZATION_STATUS_LABELS, ticket.authorizationStatus)} &rarr;
                        </Link>
                    ) : labelFor(AUTHORIZATION_STATUS_LABELS, ticket.authorizationStatus)}
                </Detail>

                {/* Migration 14 — the language the MAIL was written in, which is a
                    different question from the one the customer ASKED in (a French
                    mail can ask for the English sheet). It lives here and not with
                    the editable facts because nothing on this screen can change it:
                    it is what the agent read.

                    S2-15: the reading beside it used to be the bare decimal `0.95`.
                    `Confidence` is three dots and a word now, and it says in its
                    tooltip which of invariant 6's three numbers it is. */}
                <Detail
                    label="Mail written in"
                    title={ticket.messageLanguage
                        ? 'What the agent read the mail as being written in. It decides the language of the reply only when the mail itself named none.'
                        : 'Not recorded. Tickets created before 2026-09-01 have no reading, and the reply language falls through to the market the customer is in.'}
                >
                    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        {ticket.messageLanguage ?? '—'}
                        <Confidence value={ticket.messageLanguage ? ticket.messageLanguageConfidence : null} about="which language this mail is written in" />
                    </span>
                </Detail>

                <Detail label="Raised" title={`Created by ${ticket.createdBy}`}>
                    <span className="tabular-nums">{isoDate(ticket.createdAt)}</span>
                </Detail>

                {/* P37a's clock: the later of `UpdatedAt` and the newest thing a
                    PERSON did. The board's `Waiting` column and the stale reminder
                    read the same field, so the three cannot disagree about one
                    ticket. */}
                <Detail
                    label="Last activity"
                    title="The later of the last system change and the last thing a person did on this ticket — the same clock the Waiting column and the reminder use."
                >
                    <span className="tabular-nums">{isoDateTime(ticket.lastActivityAt ?? ticket.updatedAt)}</span>
                </Detail>

                {/* Only where the customer has actually chased: a "0" here would be
                    a figure about nothing (§3 — a missing value is a dash, and a
                    thing that never happened is not a row). */}
                {(ticket.followUpCount ?? 0) > 0 && (
                    <Detail
                        label="Customer chased"
                        title="Further mails from the same sender in the same thread. They were linked to this ticket rather than raising another one."
                    >
                        <span className="tabular-nums">{ticket.followUpCount}&times;</span>
                    </Detail>
                )}
            </dl>
        </Card>
    );
}

/**
 * The ledger for this ticket — moved verbatim out of `TicketDetail.tsx`'s
 * second tab, including the expand control and the raw `DetailJson`.
 *
 * `audit-search?ticketNumber=…&includeRun=1` is the source and not
 * `vTicketCaseFile`: that view filters `TicketId IS NOT NULL` and drops Actor,
 * DetailJson and CorrelationId, which is P9's "2 of 7 rows" finding.
 */
/**
 * How many steps the rail shows before pointing at the ledger's own screen.
 *
 * Measured, and the reason this constant exists at all: `DRA-2026-00053` has
 * **200** audit rows, and the tab this card replaced rendered every one of them.
 * Behind a tab that cost nothing, because nobody opened it; in a rail that is
 * always on screen it made the page **21'034 px** long — ten times what it was
 * before the phase whose acceptance is that the page gets shorter.
 *
 * Eight is what fits beside the work without the rail becoming the page: at 8
 * rows the History card measures about 600 px against the Details card's 209.
 * The rest is not hidden — `/audit-trail?ticketNumber=…` is one click away,
 * filtered, and it is the screen built to read a ledger on.
 */
export const HISTORY_ROWS_IN_RAIL = 8;

export function TicketHistoryCard({
    ticketNumber, entries, expandedId, onToggle,
}: {
    ticketNumber: string;
    entries: AuditEntry[];
    expandedId: string | number | null;
    onToggle: (id: string | number) => void;
}) {
    const shown = entries.slice(0, HISTORY_ROWS_IN_RAIL);
    return (
        <Card
            title="History"
            subtitle={entries.length > shown.length
                ? `The ${shown.length} most recent of ${entries.length} recorded steps`
                : `${entries.length} recorded step${entries.length === 1 ? '' : 's'}`}
        >
            {entries.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted">No audit entries yet.</p>
            ) : (
                <ul className="-my-1 divide-y divide-hair-2">
                    {shown.map(e => (
                        <li key={e.id} className="py-1.5">
                            <div className="flex items-baseline justify-between gap-2 text-xs text-muted">
                                <span className="min-w-0 truncate text-body" title={e.action}>{e.action}</span>
                                <span className="shrink-0 tabular-nums">{isoDateTime(e.occurredAt)}</span>
                            </div>
                            <div className="mt-0.5 flex items-baseline justify-between gap-2 text-xs">
                                <span className="min-w-0 truncate text-muted" title={e.actor}>{e.actor}</span>
                                <span className={`shrink-0 ${
                                    e.outcome === 'Success' ? 'text-brand-700' : e.outcome === 'Failure' ? 'text-err' : 'text-warn-ink'
                                }`}>
                                    {e.outcome}
                                </span>
                            </div>
                            {/* The correlation id is in the TITLE and not on a line of
                                its own: it is a 36-character mono string, it was costing
                                a third of every row's height, and chasing a whole run is
                                what the audit-trail screen below the list is for. */}
                            {e.detail && (
                                <Button
                                    variant="quiet"
                                    size="sm"
                                    className="-ml-2.5"
                                    title={`Correlation ID: ${e.correlationId}`}
                                    onClick={() => onToggle(e.id)}
                                >
                                    {expandedId === e.id ? 'Hide detail' : 'Show detail'}
                                </Button>
                            )}
                            {/* Wrapped, not scrolled. In the full-width tab this replaced a
                                horizontal scrollbar was tolerable; in a 392 px rail the same
                                block measured 999 px of hidden width, so reading one value
                                meant dragging. `overflow-x-auto` stays as the fallback for a
                                token with nowhere to break. */}
                            {expandedId === e.id && e.detail && (
                                <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-ctl border border-hair bg-ground p-2 text-xs text-body">
                                    {JSON.stringify(e.detail, null, 2)}
                                </pre>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {entries.length > shown.length && (
                <p className="mt-3 border-t border-hair-2 pt-3 text-xs text-muted">
                    <Link to={`/audit-trail?ticketNumber=${encodeURIComponent(ticketNumber)}`} className="text-info hover:underline">
                        All {entries.length} steps in the audit trail
                    </Link>
                    {' — filterable, and with the whole run behind each correlation id.'}
                </p>
            )}
        </Card>
    );
}
